import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function UserDialogs({
  resetTarget, setResetTarget, resetPw, setResetPw, submitReset,
  pinTarget, setPinTarget, pinVal, setPinVal, submitPin,
  reassignTarget, setReassignTarget, openItems, reassignTo, setReassignTo, submitReassign,
  users,
}) {
  return (
    <>
      <Dialog open={!!resetTarget} onOpenChange={() => setResetTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Reset Password — {resetTarget?.name}</DialogTitle><DialogDescription className="sr-only">Set a new password for this staff account</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New password (min 6 chars)</Label>
              <Input data-testid="reset-password-input" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submitReset} data-testid="reset-password-submit">Set Password</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={() => setPinTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="set-pin-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Set PIN — {pinTarget?.name}</DialogTitle><DialogDescription className="sr-only">Set a 4-digit factory login PIN for this staff account</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Employee ID: <span className="font-mono">{pinTarget?.employee_id || "—"}</span> · Setting a PIN signs the user out of all current sessions and clears an active PIN lock.</p>
            <div>
              <Label>4-digit PIN</Label>
              <Input data-testid="set-pin-input" type="text" inputMode="numeric" maxLength={4} value={pinVal} onChange={(e) => setPinVal(e.target.value.replace(/\D/g, ""))} className="font-mono tracking-[0.5em]" />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submitPin} disabled={pinVal.length !== 4} data-testid="set-pin-submit">Set PIN</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignTarget} onOpenChange={() => setReassignTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="reassign-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Reassign Open Tasks — {reassignTarget?.name}</DialogTitle><DialogDescription className="sr-only">Reassign all open operational tasks to another user</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {!openItems ? <p className="text-sm text-muted-foreground">Loading open items…</p> : (
              <>
                <p className="text-sm" data-testid="reassign-open-count">
                  <span className="metric font-bold">{openItems.total_open}</span> open item(s)
                  {openItems.total_open > 0 && ` — ${openItems.ops_tasks.length} operational, ${openItems.office_tasks.length} office`}
                </p>
                {openItems.ops_tasks.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {openItems.ops_tasks.map((t) => (
                      <p key={t.task_id} className="text-xs font-mono text-muted-foreground" data-testid={`open-task-${t.task_id}`}>{t.task_id} · {t.type} · {t.bill_no || "—"} · {t.status}</p>
                    ))}
                  </div>
                )}
                <Label>Reassign operational tasks to</Label>
                <Select value={reassignTo} onValueChange={setReassignTo}>
                  <SelectTrigger data-testid="reassign-to-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {users.filter((u) => u.user_id !== reassignTarget?.user_id && u.status === "active").map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={submitReassign} data-testid="reassign-submit" disabled={!reassignTo}>Reassign Tasks</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
